import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { api, euro, shortDate } from "../lib/api";
import { toast } from "sonner";
import {
    Warning,
    ArrowRight,
    LockKey,
    Users,
    Buildings,
    Tag,
    UserCircle,
    CheckCircle,
    Eye,
    ChartLine,
    ChartBar,
} from "@phosphor-icons/react";
import {
    ResponsiveContainer,
    BarChart,
    Bar,
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
} from "recharts";

const PRESETS = [
    { key: "current_month", label: "Mese corrente" },
    { key: "last_month", label: "Mese scorso" },
    { key: "quarter", label: "Trimestre" },
    { key: "semester", label: "Semestre" },
    { key: "year", label: "Anno" },
    { key: "custom", label: "Personalizzato" },
];

function ymFromIso(iso) {
    return iso ? iso.slice(0, 7) : "";
}

function formatBucketLabel(bucket, granularity) {
    if (!bucket) return "";
    if (granularity === "month") {
        const [y, m] = bucket.split("-");
        return `${m}/${y.slice(2)}`;
    }
    if (granularity === "week") {
        const d = new Date(bucket + "T00:00:00");
        return d.toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit" });
    }
    const d = new Date(bucket + "T00:00:00");
    return d.toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit" });
}

export default function Reports() {
    const { agencyId } = useAuth();
    const [preset, setPreset] = useState("current_month");
    const [customStart, setCustomStart] = useState("");
    const [customEnd, setCustomEnd] = useState("");
    const [chartMode, setChartMode] = useState("rev_vs_exp"); // "rev_vs_exp" | "pnl"
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [closureInfo, setClosureInfo] = useState(null);

    const load = () => {
        if (!agencyId) return;
        if (preset === "custom" && (!customStart || !customEnd)) return;
        setLoading(true);
        const params = { agency_id: agencyId, preset };
        if (preset === "custom") {
            params.start = new Date(customStart + "T00:00:00").toISOString();
            params.end = new Date(customEnd + "T23:59:59").toISOString();
        }
        api.get("/reports/analytics", { params })
            .then((r) => setData(r.data))
            .catch(() => toast.error("Errore nel caricamento report"))
            .finally(() => setLoading(false));
    };

    useEffect(() => { load(); // eslint-disable-line react-hooks/exhaustive-deps
    }, [agencyId, preset, customStart, customEnd]);

    // Load closure info only for month-based presets
    useEffect(() => {
        if (!agencyId || !data) { setClosureInfo(null); return; }
        if (!(preset === "current_month" || preset === "last_month")) {
            setClosureInfo(null);
            return;
        }
        const ym = ymFromIso(data.range.start);
        api.get("/reports/pre-closure", { params: { agency_id: agencyId, year_month: ym } })
            .then((r) => setClosureInfo(r.data))
            .catch(() => setClosureInfo(null));
    }, [agencyId, data?.range?.start, preset]); // eslint-disable-line react-hooks/exhaustive-deps

    const chartData = useMemo(() => {
        if (!data?.series) return [];
        return data.series.map((b) => ({
            ...b,
            label: formatBucketLabel(b.bucket, data.range.granularity),
        }));
    }, [data]);

    if (!data) return <div className="overline p-12">{loading ? "Caricamento…" : "—"}</div>;

    const { kpis, range, top5, alerts } = data;
    const errorsCount = alerts.blockers.filter((b) => b.level === "error").length;
    const warningsCount = alerts.blockers.filter((b) => b.level === "warning").length;
    const hasAlerts =
        alerts.blockers.length > 0 ||
        alerts.sospesi.count > 0 ||
        alerts.providers_fido_alto.length > 0 ||
        alerts.clients_fido_alto.length > 0;

    return (
        <div data-testid="reports-page">
            {/* -------- HEADER -------- */}
            <div className="mb-8">
                <div className="overline mb-2">Analisi periodo</div>
                <h1 className="text-4xl md:text-5xl font-black tracking-tighter">
                    Report — {range.label}
                </h1>
                <div className="text-sm text-zinc-600 mt-1 flex items-center gap-3">
                    <span>
                        {new Date(range.start).toLocaleDateString("it-IT")} →{" "}
                        {new Date(range.end).toLocaleDateString("it-IT")}
                    </span>
                    <span className="text-zinc-400">·</span>
                    <span className="uppercase tracking-wider text-[10px] font-bold text-zinc-500">
                        {kpis.n_transactions} tx · {range.granularity === "day" ? "giornaliera" : range.granularity === "week" ? "settimanale" : "mensile"}
                    </span>
                    {closureInfo?.already_closed && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-50 text-green-700 text-[10px] font-bold uppercase tracking-wider border border-green-200">
                            <CheckCircle size={10} weight="fill" /> mese chiuso
                        </span>
                    )}
                </div>
            </div>

            {/* -------- PERIOD SELECTOR -------- */}
            <div className="mb-8 bg-white border border-zinc-200 p-4 flex flex-wrap items-end gap-3">
                <div className="flex flex-wrap gap-2" data-testid="period-presets">
                    {PRESETS.map((p) => (
                        <button
                            key={p.key}
                            onClick={() => setPreset(p.key)}
                            data-testid={`preset-${p.key}`}
                            className={`px-3 py-2 text-xs font-bold uppercase tracking-wider border transition ${
                                preset === p.key
                                    ? "bg-ink text-white border-ink"
                                    : "bg-white text-zinc-700 border-zinc-200 hover:border-ink"
                            }`}
                        >
                            {p.label}
                        </button>
                    ))}
                </div>
                {preset === "custom" && (
                    <div className="flex items-end gap-3 ml-auto">
                        <label className="block">
                            <div className="overline mb-1">Da</div>
                            <input
                                type="date"
                                className="inp"
                                value={customStart}
                                onChange={(e) => setCustomStart(e.target.value)}
                                data-testid="custom-start"
                            />
                        </label>
                        <label className="block">
                            <div className="overline mb-1">A</div>
                            <input
                                type="date"
                                className="inp"
                                value={customEnd}
                                onChange={(e) => setCustomEnd(e.target.value)}
                                data-testid="custom-end"
                            />
                        </label>
                    </div>
                )}
                {closureInfo?.already_closed && closureInfo.existing_closure && (
                    <Link
                        to={`/closures/${closureInfo.existing_closure.id}`}
                        className="btn-secondary flex items-center gap-2 ml-auto"
                        data-testid="view-closure-detail"
                    >
                        <Eye size={14} weight="bold" /> Dettaglio chiusura
                    </Link>
                )}
                {closureInfo && !closureInfo.already_closed && !closureInfo.is_future &&
                 closureInfo.blockers.filter((b) => b.level === "error").length === 0 && (
                    <Link
                        to="/closures"
                        className="btn-primary flex items-center gap-2 ml-auto"
                        data-testid="go-to-closures"
                    >
                        <LockKey size={14} weight="bold" /> Vai alla chiusura{" "}
                        <ArrowRight size={14} weight="bold" />
                    </Link>
                )}
            </div>

            {/* -------- KPI CARDS -------- */}
            <div className="mb-8 bg-white border border-zinc-200">
                <div className="grid grid-cols-2 md:grid-cols-5 grid-room" data-testid="kpi-cards">
                    <KpiCell label="Entrate" value={kpis.entrate} color="#00C853" sub={`di cui accrediti ${euro(kpis.accrediti)}`} />
                    <KpiCell label="Uscite" value={kpis.uscite} color="#FF2A00" />
                    <KpiCell
                        label="P&L"
                        value={kpis.pnl}
                        color={kpis.pnl >= 0 ? "#00C853" : "#FF2A00"}
                        sub={kpis.margin_pct !== null ? `margine ${kpis.margin_pct.toFixed(1)}%` : null}
                    />
                    <KpiCell
                        label="Accantonato atteso"
                        value={kpis.pnl > 0 ? kpis.pnl : 0}
                        sub="→ Utili Accantonati"
                    />
                    <KpiCell label="Transazioni" value={kpis.n_transactions} isNumber />
                </div>
            </div>

            {/* -------- TREND CHART -------- */}
            <div className="mb-8 bg-white border border-zinc-200">
                <div className="px-6 py-4 border-b border-zinc-200 flex items-center justify-between flex-wrap gap-2">
                    <div className="overline flex items-center gap-2">
                        <ChartLine size={12} weight="bold" /> Andamento
                    </div>
                    <div className="flex gap-1" data-testid="chart-mode-toggle">
                        <button
                            onClick={() => setChartMode("rev_vs_exp")}
                            data-testid="chart-mode-rev-exp"
                            className={`px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider border flex items-center gap-1.5 transition ${
                                chartMode === "rev_vs_exp"
                                    ? "bg-ink text-white border-ink"
                                    : "bg-white text-zinc-700 border-zinc-200 hover:border-ink"
                            }`}
                        >
                            <ChartBar size={11} weight="bold" /> Ricavi vs Spese
                        </button>
                        <button
                            onClick={() => setChartMode("pnl")}
                            data-testid="chart-mode-pnl"
                            className={`px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider border flex items-center gap-1.5 transition ${
                                chartMode === "pnl"
                                    ? "bg-ink text-white border-ink"
                                    : "bg-white text-zinc-700 border-zinc-200 hover:border-ink"
                            }`}
                        >
                            <ChartLine size={11} weight="bold" /> Utile netto
                        </button>
                    </div>
                </div>
                <div className="p-4" style={{ height: 340 }}>
                    {chartData.length === 0 ? (
                        <div className="h-full flex items-center justify-center text-sm text-zinc-400">
                            Nessun dato per il periodo selezionato
                        </div>
                    ) : chartMode === "rev_vs_exp" ? (
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" vertical={false} />
                                <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#71717a" }} tickLine={false} axisLine={{ stroke: "#e4e4e7" }} />
                                <YAxis tick={{ fontSize: 11, fill: "#71717a" }} tickLine={false} axisLine={false} tickFormatter={(v) => `${(v / 1000).toFixed(v >= 1000 ? 1 : 0)}k`} />
                                <Tooltip
                                    formatter={(v, name) => [euro(v), name === "entrate" ? "Ricavi" : "Spese"]}
                                    contentStyle={{ border: "1px solid #09090b", borderRadius: 0, fontSize: 12 }}
                                    cursor={{ fill: "rgba(9,9,11,0.04)" }}
                                />
                                <Legend
                                    formatter={(v) => (v === "entrate" ? "Ricavi" : "Spese")}
                                    wrapperStyle={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.05em", fontWeight: 700 }}
                                />
                                <Bar dataKey="entrate" fill="#00C853" name="entrate" />
                                <Bar dataKey="uscite" fill="#FF2A00" name="uscite" />
                            </BarChart>
                        </ResponsiveContainer>
                    ) : (
                        <ResponsiveContainer width="100%" height="100%">
                            <LineChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" vertical={false} />
                                <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#71717a" }} tickLine={false} axisLine={{ stroke: "#e4e4e7" }} />
                                <YAxis tick={{ fontSize: 11, fill: "#71717a" }} tickLine={false} axisLine={false} tickFormatter={(v) => `${(v / 1000).toFixed(v >= 1000 ? 1 : 0)}k`} />
                                <Tooltip
                                    formatter={(v) => [euro(v), "Utile netto"]}
                                    contentStyle={{ border: "1px solid #09090b", borderRadius: 0, fontSize: 12 }}
                                />
                                <Line type="monotone" dataKey="pnl" stroke="#09090b" strokeWidth={2.5} dot={{ r: 3, fill: "#09090b" }} activeDot={{ r: 5 }} />
                            </LineChart>
                        </ResponsiveContainer>
                    )}
                </div>
            </div>

            {/* -------- TOP 5 -------- */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8" data-testid="top5-grid">
                <TopCard
                    title="Top categorie di spesa"
                    icon={<Tag size={12} weight="bold" />}
                    rows={top5.categories_spese}
                    valueKey="total"
                    labelKey="category"
                    formatValue={(v) => euro(v)}
                    emptyMsg="Nessuna spesa nel periodo"
                />
                <TopCard
                    title="Top fornitori per accrediti"
                    icon={<Buildings size={12} weight="bold" />}
                    rows={top5.providers_accrediti}
                    valueKey="total"
                    labelKey="name"
                    formatValue={(v) => euro(v)}
                    emptyMsg="Nessun accredito nel periodo"
                />
                <TopCard
                    title="Top clienti per debito"
                    icon={<Users size={12} weight="bold" />}
                    rows={top5.clients_debito}
                    valueKey="debt"
                    labelKey="name"
                    formatValue={(v) => euro(v)}
                    emptyMsg="Nessun debito cliente aperto"
                />
                <TopCard
                    title="Top operatori per attività"
                    icon={<UserCircle size={12} weight="bold" />}
                    rows={top5.operators}
                    valueKey="count"
                    labelKey="name"
                    formatValue={(v) => `${v} tx`}
                    emptyMsg="Nessuna attività"
                />
            </div>

            {/* -------- ALERTS (in fondo) -------- */}
            {hasAlerts ? (
                <div className="mb-8 bg-white border border-zinc-200">
                    <div className="px-6 py-4 border-b border-zinc-200 overline flex items-center justify-between">
                        <span className="flex items-center gap-2">
                            <Warning size={12} weight="bold" /> Alert operativi
                        </span>
                        <span className="text-zinc-500">
                            {errorsCount > 0 && <span className="text-red-600 mr-3">{errorsCount} errori</span>}
                            {warningsCount > 0 && <span className="text-amber-700">{warningsCount} warning</span>}
                        </span>
                    </div>
                    <ul className="divide-y divide-zinc-100" data-testid="alerts-list">
                        {alerts.blockers.map((b, i) => (
                            <li key={`bl-${i}`} className="px-6 py-3 flex items-center gap-3 text-sm">
                                <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: b.level === "error" ? "#FF2A00" : "#F59E0B" }} />
                                <div className="flex-1">{b.message}</div>
                            </li>
                        ))}
                        {alerts.sospesi.count > 0 && (
                            <li className="px-6 py-3 flex items-center gap-3 text-sm">
                                <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: "#F59E0B" }} />
                                <div className="flex-1">
                                    <strong>{alerts.sospesi.count}</strong> sospeso/i aperti per un totale di{" "}
                                    <strong>{euro(alerts.sospesi.total)}</strong>
                                </div>
                                <Link to="/clients" className="text-xs text-zinc-500 hover:text-ink font-bold uppercase tracking-wider" data-testid="go-to-clients">
                                    Vedi clienti →
                                </Link>
                            </li>
                        )}
                        {alerts.providers_fido_alto.map((p) => (
                            <li key={`p-${p.provider_id}`} className="px-6 py-3 flex items-center gap-3 text-sm">
                                <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: p.pct >= 100 ? "#FF2A00" : "#F59E0B" }} />
                                <div className="flex-1">
                                    Fornitore <strong>{p.name}</strong> a {p.pct}% del fido ({euro(p.outstanding)}/{euro(p.fido)})
                                </div>
                                <Link to="/providers" className="text-xs text-zinc-500 hover:text-ink font-bold uppercase tracking-wider">
                                    Vedi →
                                </Link>
                            </li>
                        ))}
                        {alerts.clients_fido_alto.map((c) => (
                            <li key={`c-${c.client_id}`} className="px-6 py-3 flex items-center gap-3 text-sm">
                                <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: c.pct >= 100 ? "#FF2A00" : "#F59E0B" }} />
                                <div className="flex-1">
                                    Cliente <strong>{c.name}</strong> a {c.pct}% del fido ({euro(c.debt)}/{euro(c.fido)})
                                </div>
                                <Link to="/clients" className="text-xs text-zinc-500 hover:text-ink font-bold uppercase tracking-wider">
                                    Vedi →
                                </Link>
                            </li>
                        ))}
                    </ul>
                </div>
            ) : (
                <div className="mb-8 bg-green-50 border border-green-200 p-4 flex items-center gap-3 text-sm text-green-800">
                    <CheckCircle size={18} weight="fill" />
                    Nessun alert operativo per il periodo selezionato.
                </div>
            )}

            {closureInfo?.already_closed && closureInfo.existing_closure && (
                <div className="text-xs text-zinc-500 mb-8">
                    ✓ Mese chiuso da {closureInfo.existing_closure.closed_by} · {shortDate(closureInfo.existing_closure.closed_at)}
                </div>
            )}
        </div>
    );
}

function KpiCell({ label, value, color, sub, isNumber }) {
    return (
        <div className="p-6 grid-cell">
            <div className="overline mb-2">{label}</div>
            <div className="text-2xl font-black tabular-nums" style={color ? { color } : undefined}>
                {isNumber ? value : euro(value)}
            </div>
            {sub && <div className="text-xs text-zinc-500 mt-1 tabular-nums">{sub}</div>}
        </div>
    );
}

function TopCard({ title, icon, rows, valueKey, labelKey, formatValue, emptyMsg }) {
    const max = rows.reduce((m, r) => Math.max(m, r[valueKey] || 0), 0) || 1;
    return (
        <div className="bg-white border border-zinc-200">
            <div className="px-6 py-4 border-b border-zinc-200 overline flex items-center gap-2">
                {icon}
                {title}
            </div>
            {rows.length === 0 ? (
                <div className="p-10 text-center text-zinc-400 text-sm">{emptyMsg}</div>
            ) : (
                <ul className="divide-y divide-zinc-100">
                    {rows.map((r, i) => {
                        const pct = (r[valueKey] / max) * 100;
                        return (
                            <li key={r[labelKey] || i} className="px-6 py-3 flex items-center gap-3" data-testid={`top-row-${i}`}>
                                <div className="text-xs font-bold tabular-nums text-zinc-400 w-4">{i + 1}</div>
                                <div className="flex-1 min-w-0">
                                    <div className="text-sm font-bold truncate">{r[labelKey]}</div>
                                    <div className="h-1 bg-zinc-100 mt-1 relative">
                                        <div className="h-full bg-ink absolute left-0 top-0" style={{ width: `${pct}%`, background: "#09090b" }} />
                                    </div>
                                </div>
                                <div className="text-sm font-black tabular-nums">{formatValue(r[valueKey])}</div>
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
}
